import Typography from "components/typography";

import React from "react";
import styled from "styled-components";
import PlaceHolderAudio from "constants/placeholderMusic.mp3";
import { Colors } from "utils/colors";
import { getStoreIcon } from "utils/getStoreIcon";
import Player from "./player";
import { ILink, IPreviewClip } from "types/ratedfansData";
import { storeIcons } from "constants/storeIcons";

interface IPlayItem {
  storeName: string;
  link: string;
}

interface IProps {
  preSave: boolean;
  links: ILink[];
  previewClips: IPreviewClip[];
}

const TrackPlayer = ({ preSave, links, previewClips }: IProps) => {
  const PaidFor = ["Amazon Music", "iTunes"];

  const PlayItem = ({ storeName, link }: IPlayItem) => {
    const storeIconKey = Object.keys(storeIcons).find(
      (icon) => icon.toLowerCase() === storeName.toLowerCase()
    );
    const storeIcon = storeIconKey
      ? storeIcons[storeIconKey as keyof typeof storeIcons]
      : null;

    return (
      <PlayItemContainer onClick={() => window.open(link, "_blank")}>
        <IconContainer>
          <IconInner>{storeIcon}</IconInner>
        </IconContainer>
        <Typography ml="3.2" size="2xl" weight="medium" m_size="m" m_ml="2.4">
          {storeName}
        </Typography>
        <PlayButton>
          <PlayIcon>
            {storeName === "iTunes" || storeName === "Amazon Music" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5.50096 3.99992L5.50106 4.00985C4.64289 4.03561 4.11778 4.12819 3.71688 4.4609C3.16143 4.92189 3.01512 5.70218 2.72251 7.26279L2.22251 9.92945L2.2225 9.92954C1.81105 12.1239 1.60533 13.2211 2.20513 13.9439C2.80494 14.6666 3.92129 14.6666 6.154 14.6666H9.84791C12.0806 14.6666 13.197 14.6666 13.7968 13.9439C14.3966 13.2211 14.1909 12.1239 13.7794 9.92945L13.2794 7.26279L13.2793 7.26213C12.9867 5.70196 12.8404 4.92182 12.285 4.4609C11.8842 4.12819 11.359 4.03561 10.5008 4.00985L10.501 3.99992V3.33325C10.501 1.95254 9.38171 0.833252 8.00097 0.833252C6.62025 0.833252 5.50096 1.95254 5.50096 3.33325V3.99992ZM8.00097 1.83325C7.17251 1.83325 6.50096 2.50483 6.50096 3.33325V3.99993L6.654 3.99992H9.34791H9.50097V3.33325C9.50097 2.50483 8.82937 1.83325 8.00097 1.83325ZM9.26948 9.79957C9.56729 9.63762 9.56729 9.21839 9.26948 9.05643L7.47131 8.0786C7.18187 7.9212 6.82617 8.12606 6.82617 8.45016V10.4058C6.82617 10.7299 7.18187 10.9348 7.47131 10.7774L9.26948 9.79957Z"
                  fill="white"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M14.2718 6.23497C15.6864 7.00425 15.6864 8.99559 14.2718 9.76485L5.73043 14.4096C4.35558 15.1573 2.66602 14.1841 2.66602 12.6447V3.35519C2.66602 1.81571 4.35558 0.842598 5.73042 1.59024L14.2718 6.23497Z"
                  fill="white"
                />
              </svg>
            )}
          </PlayIcon>
          <Typography m_size="s">
            {preSave && PaidFor.includes(storeName)
              ? "PRE-ORDER"
              : preSave
              ? "PRE-SAVE"
              : PaidFor.includes(storeName)
              ? "BUY"
              : "PLAY"}
          </Typography>
        </PlayButton>
      </PlayItemContainer>
    );
  };

  return (
    <Container>
      {preSave && previewClips.length > 0 && (
        <Player previewClips={previewClips} />
      )}

      {links.map(({ platform, url }, i) => {
        return <PlayItem key={i} storeName={platform} link={url} />;
      })}
    </Container>
  );
};

export default TrackPlayer;

const Container = styled.div`
  width: 40%;
  border-radius: 0.8rem;
  overflow: hidden;
  height: fit-content;
  background-color: ${Colors.grey700};
  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const PlayItemContainer = styled.div`
  border-top: 1px solid #434343;
  width: 100%;
  padding: 0.8rem;
  padding-right: 4rem;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const IconContainer = styled.div`
  width: 6.4rem;
  height: 6.4rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 0.257px solid rgba(255, 255, 255, 0.4);
  background: linear-gradient(0deg, #000 0%, #000 100%),
    rgba(128, 128, 128, 0.3);
  background-blend-mode: overlay, luminosity;
  backdrop-filter: blur(9.195403099060059px);

  @media only screen and (max-width: 769px) {
    width: 4.8rem;
    height: 4.8rem;
  }
`;

const IconInner = styled.div`
  width: 4rem;
  height: 4rem;
  @media only screen and (max-width: 769px) {
    width: 3rem;
    height: 3rem;
  }
`;

const PlayButton = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const PlayIcon = styled.div`
  width: 1.6rem;
  height: 1.6rem;
  margin-right: 0.8rem;
  @media only screen and (max-width: 769px) {
    width: 1.4rem;
    height: 1.4rem;
  }
`;
